import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarDays, Clock3, LoaderCircle, Minus } from 'lucide-react';
import { useState } from 'react';
import { api } from '../../api/client';
import type { StateUpdate } from '../../api/types';
import type { MultiStateSelection } from '../../app/AppOutletContext';
import { DateInput } from '../../components/DateInput';
import { LegacyStatusIcon } from '../../components/LegacyIcon';
import { Modal } from '../../components/Modal';
import { PersonCombobox } from '../../components/PersonCombobox';
import { RichTextEditor } from '../../components/RichTextEditor';
import { STATE_TYPES, fromDateInput } from '../../lib/status';

const UNCHANGED = '__unchanged';
const CLEAR = '__clear';

function optionalPerson(value: string): number | null | undefined {
  if (value === UNCHANGED) return undefined;
  if (value === CLEAR) return null;
  return Number(value);
}

export function MultiStateEditorModal({ selection, onClose }: { selection: MultiStateSelection | null; onClose: () => void }) {
  const stateIds = selection?.stateIds ?? [];
  const projectId = selection?.projectId;
  const queryClient = useQueryClient();
  const [type, setType] = useState(UNCHANGED);
  const [executor, setExecutor] = useState(UNCHANGED);
  const [examiner, setExaminer] = useState(UNCHANGED);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [datesValid, setDatesValid] = useState({ start: true, end: true });
  const [planHours, setPlanHours] = useState('');
  const [factHours, setFactHours] = useState('');
  const [comment, setComment] = useState('');
  const usersQuery = useQuery({
    queryKey: ['users', 'multi-state', projectId],
    queryFn: () => api.users({ projectId }),
    enabled: stateIds.length > 0 && Number.isInteger(projectId) && Number(projectId) > 0,
  });
  const mutation = useMutation({
    mutationFn: () => {
      const update: StateUpdate = {};
      if (type !== UNCHANGED) update.type = type === CLEAR ? null : type;
      const executorId = optionalPerson(executor);
      const examinerId = optionalPerson(examiner);
      if (executorId !== undefined) update.executor_id = executorId;
      if (examinerId !== undefined) update.examiner_id = examinerId;
      if (startDate) update.start_date = fromDateInput(startDate);
      if (endDate) update.end_date = fromDateInput(endDate);
      if (planHours) update.plan_hours = Number(planHours);
      if (factHours) update.fact_hours = Number(factHours);
      if (comment) update.comment = comment;
      return api.updateStates(stateIds, update);
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['states'] }),
        queryClient.invalidateQueries({ queryKey: ['assets'] }),
      ]);
      onClose();
    },
  });

  if (!stateIds.length) return null;
  const validDates = datesValid.start && datesValid.end;
  const groupedTargets = selection?.targets.reduce<Array<{ title: string; items: string[] }>>((groups, target) => {
    const title = selection.groupBy === 'asset' ? target.assetTitle : target.stepTitle;
    const item = selection.groupBy === 'asset' ? target.stepTitle : target.assetTitle;
    const current = groups.at(-1);
    if (current?.title === title) current.items.push(item);
    else groups.push({ title, items: [item] });
    return groups;
  }, []) ?? [];
  const assetCount = new Set(selection?.targets.map((target) => target.assetTitle)).size;
  const stepCount = new Set(selection?.targets.map((target) => target.stepTitle)).size;

  return (
    <Modal
      className="multi-state-modal"
      onClose={onClose}
      size="large"
      title={`Множественное редактирование · ${stateIds.length}`}
    >
      <div className="multi-state-editor">
        <section className="multi-state-editor__form">
          <details className="multi-state-selection">
            <summary>
              Выбрано задач: <b>{stateIds.length}</b> · ассетов: <b>{assetCount}</b> · этапов: <b>{stepCount}</b>
            </summary>
            <div className="multi-state-selection__groups">
              {groupedTargets.map((group) => (
                <section key={group.title}>
                  <strong>{selection?.groupBy === 'asset' ? 'Ассет' : 'Этап'}: {group.title}</strong>
                  <span>{group.items.join(', ')}</span>
                </section>
              ))}
            </div>
          </details>
          <p>Изменятся только заполненные поля.</p>
          <div className="status-picker multi-status-picker" role="radiogroup" aria-label="Статус">
            <label className="status-choice status-choice--unchanged">
              <input checked={type === UNCHANGED} name="multi-state-type" onChange={() => setType(UNCHANGED)} type="radio" />
              <Minus aria-hidden="true" size={40} />
              <span>не изменять</span>
            </label>
            {STATE_TYPES.map((state) => (
              <label className={`status-choice status-choice--${state.name}`} key={state.name}>
                <input checked={type === state.text} name="multi-state-type" onChange={() => setType(state.text)} type="radio" />
                <LegacyStatusIcon large name={state.name} off={type !== state.text} />
                <span>{state.text}</span>
              </label>
            ))}
            <label className="status-choice status-choice--empty">
              <input checked={type === CLEAR} name="multi-state-type" onChange={() => setType(CLEAR)} type="radio" />
              <LegacyStatusIcon large name="empty" off={type !== CLEAR} />
              <span>пусто</span>
            </label>
          </div>

          <div className="state-editor__fields">
            <div className="state-editor__assignees">
              <PersonCombobox
                emptyValue={CLEAR}
                label="Исполнитель"
                nullLabel="Не назначен"
                onChange={setExecutor}
                unchangedOption={{ label: 'Не изменять', value: UNCHANGED }}
                users={usersQuery.data ?? []}
                value={executor}
              />
              <PersonCombobox
                emptyValue={CLEAR}
                label="Ответственный"
                nullLabel="Не назначен"
                onChange={setExaminer}
                unchangedOption={{ label: 'Не изменять', value: UNCHANGED }}
                users={usersQuery.data ?? []}
                value={examiner}
              />
            </div>
            <div className="state-editor__schedule">
              <div className="state-pair-row">
                <strong><CalendarDays size={14} /> Сроки</strong>
                <label className="field">
                  <span>Начало</span>
                  <DateInput ariaLabel="Начало" onChange={setStartDate} onValidChange={(start) => setDatesValid((current) => ({ ...current, start }))} value={startDate} />
                </label>
                <label className="field">
                  <span>Окончание</span>
                  <DateInput ariaLabel="Окончание" onChange={setEndDate} onValidChange={(end) => setDatesValid((current) => ({ ...current, end }))} value={endDate} />
                </label>
              </div>
              <div className="state-pair-row">
                <strong><Clock3 size={14} /> Часы</strong>
                <label className="field"><span>План</span><input min="0" onChange={(event) => setPlanHours(event.target.value)} step="0.5" type="number" value={planHours} /></label>
                <label className="field"><span>Факт</span><input min="0" onChange={(event) => setFactHours(event.target.value)} step="0.5" type="number" value={factHours} /></label>
              </div>
            </div>
          </div>

          <RichTextEditor minHeight={72} onChange={setComment} placeholder="Комментарий к изменению" value={comment} />
          <div className="state-editor__actions">
            {!validDates ? <span className="form-error">Введите дату в формате дд.мм.гггг</span> : null}
            {mutation.error ? <span className="form-error">{mutation.error.message}</span> : null}
            <button className="button button--primary" disabled={mutation.isPending || !validDates} onClick={() => mutation.mutate()} type="button">
              {mutation.isPending ? <LoaderCircle className="spin" size={15} /> : null}
              Сохранить
            </button>
          </div>
        </section>
      </div>
    </Modal>
  );
}
